import React, { Fragment, useEffect, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useHistory } from "react-router-dom";
import { API_ROUTES } from "../api-routes";
import isValidPhoneNumber from "libphonenumber-js";
import parsePhoneNumberFromString from "libphonenumber-js";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { ApiConsumer } from "api/ApiConsumer";
import { Helmet } from "react-helmet";
import Header from "components/header";
import Footer from "components/footer";
import { CONSTANTS } from "../../../contants/constants";
import Modals from "components/modals/index";
// Import Google Places Autocomplete from new codebase
import GooglePlacesAutocomplete from "../../../forms/google-places-auto-complete";

// Registration schema (step 1)
const registerSchema = z
  .object({
    account: z.string({ required_error: "Account number is required." }),
    registerIDType: z
      .enum(["SAID", "PASS", "NAID", "BWID"], {
        required_error: "Please select an ID type.",
      })
      .refine((val) => val !== "selected", {
        message: "Please select an ID type.",
      }),
    registerID: z
      .string({ required_error: "Please enter your ID/Passport number." })
      .superRefine((val, ctx) => {
        const idType = ctx.parent?.registerIDType;
        if (!idType) return true;
        if (idType !== "PASS") {
          if (!val || val.length !== 13) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "ID number must be exactly 13 digits",
            });
            return false;
          }
          if (!/^\d{13}$/.test(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "ID number must be numeric and exactly 13 digits",
            });
            return false;
          }
        } else {
          if (!val || val.trim() === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Passport number is required",
            });
            return false;
          }
        }
        return true;
      }),
    registerTitle: z
      .enum(["Mr", "Mrs", "Mx", "Miss", "Ms", "Prof", "Dr", "Sir"], {
        required_error: "Please select a title.",
      })
      .refine((val) => val !== "selected", {
        message: "Please select a title.",
      }),
    registerFirstname: z
      .string({ required_error: "Please enter your first name" })
      .min(2, "First name must be at least two characters long!"),
    registerLastname: z.string({
      required_error: "Please enter your last name.",
    }),
    registerCellNumber: z
      .string({ required_error: "Phone number is required to proceed." })
      .refine(
        (value) => {
          const phoneNumber = isValidPhoneNumber(value);
          return phoneNumber && phoneNumber.isValid();
        },
        { message: "Invalid phone number" }
      ),
    registerEmail: z
      .string({ required_error: "Email address is required." })
      .email("Please enter a valid email address."),
    registerConfirmationEmail: z
      .string({ required_error: "Confirmation email is required." })
      .email("Please enter a valid confirmation email."),
    registerLicenceCode: z
      .enum(["B", "C1", "C", "EB", "EC1", "EC", "Dr", "INT"], {
        required_error: "Please select a license code.",
      })
      .refine((val) => val !== "selected", {
        message: "Please select a valid license code.",
      }),
    registerDriverLicenceNumber: z
      .string({ required_error: "Driver Licence Number is required" })
      .regex(
        /^[A-Z0-9]{6,15}$/,
        "Driver Licence Number must be alphanumeric and between 6 and 15 characters"
      ),
    registerPassword: z
      .string({ required_error: "Please enter your password." })
      .min(8, "Password must be at least 8 characters."),
    registerRePassword: z.string({
      required_error: "Please confirm your password.",
    }),
    consent: z
      .boolean({
        required_error: "You must agree to terms and conditions to proceed.",
      })
      .refine((val) => val === true, {
        message: "You must agree to terms and conditions to proceed.",
      }),
  })
  .refine((fields) => fields.registerPassword === fields.registerRePassword, {
    message: "Please enter matching passwords",
    path: ["registerRePassword"],
  })
  .refine(
    (fields) => fields.registerEmail === fields.registerConfirmationEmail,
    {
      message: "Please enter matching emails",
      path: ["registerConfirmationEmail"],
    }
  );

// Address schema (step 2)
const addressSchema = z.object({
  homeAddress: z.string().min(1, "Please select a home address"),
  homeAddressLine1: z.string().min(1, "Address Line 1 is required"),
  homeAddressLine2: z.string().optional(),
  homeCity: z.string().min(1, "City is required"),
  homeProvince: z.string().min(1, "Province is required"),
  homePostCode: z.string().min(1, "Postal code is required"),
  homeCountry: z.string().min(1, "Country is required"),
  billingAddress: z.string().optional(),
  billingAddressLine1: z.string().optional(),
  billingAddressLine2: z.string().optional(),
  billingCity: z.string().optional(),
  billingProvince: z.string().optional(),
  billingPostCode: z.string().optional(),
  billingCountry: z.string().optional(),
});

const RegisterPage = () => {
  const history = useHistory();
  const [registerError, setRegisterError] = useState("");
  const [validRegisterDetails, setValidRegisterDetails] = useState({});
  const [registerDetails, setRegisterDetails] = useState({});
  const [termsChecked, setTermsChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);
  const [phoneNo, setPhoneNo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [visibleModal, setVisibleModal] = useState("");
  const [pagePostModalSlug, setPagePostModalSlug] = useState("");
  const [loginAccountType, setLoginAccountType] = useState(
    CONSTANTS.LOGIN_ACCOUNT_TYPE.CORPORATE
  );
  // Two-step registration
  const [showAddressSection, setShowAddressSection] = useState(false);
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [formKey, setFormKey] = useState(0);
  const [apiToken, setApiToken] = useState("");

  // Step 1: Registration form
  const {
    control: registrationControl,
    handleSubmit: handleRegistrationSubmit,
    formState: { errors: registrationErrors },
    trigger: triggerRegistration,
    getValues: getRegistrationValues,
    reset: resetRegistrationForm,
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: "onChange",
  });

  // Step 2: Address form
  const {
    control: addressControl,
    handleSubmit: handleAddressSubmit,
    setValue: setAddressValue,
    formState: { errors: addressErrors },
    trigger: triggerAddress,
    getValues: getAddressValues,
    reset: resetAddressForm,
    unregister,
  } = useForm({
    resolver: zodResolver(addressSchema),
    mode: "onChange",
    defaultValues: {
      homeAddress: "",
      homeAddressLine1: "",
      homeAddressLine2: "",
      homeCity: "",
      homeProvince: "",
      homePostCode: "",
      billingAddress: "",
      billingAddressLine1: "",
      billingAddressLine2: "",
      billingCity: "",
      billingProvince: "",
      billingPostCode: "",
    },
  });

  useEffect(() => {
    setRegisterDetails((prevState) => ({
      ...prevState,
      [`SITE_HOST`]: "https://www.bluucarrental.com",
    }));
  }, []);

  useEffect(() => {
    setRegisterError("");
    setValidRegisterDetails({});
  }, [registerDetails]);

  // Function to clear address form
  const clearAddressForm = useCallback(() => {
    const emptyValues = {
      homeAddress: "",
      homeAddressLine1: "",
      homeAddressLine2: "",
      homeCity: "",
      homeProvince: "",
      homePostCode: "",
      billingAddress: "",
      billingAddressLine1: "",
      billingAddressLine2: "",
      billingCity: "",
      billingProvince: "",
      billingPostCode: "",
    };

    // Unregister all fields first
    Object.keys(emptyValues).forEach((field) => {
      addressControl.unregister(field);
    });

    // Reset form with empty values
    resetAddressForm(emptyValues, {
      keepDefaultValues: false,
      keepValues: false,
    });

    // Explicitly set each field to empty
    Object.keys(emptyValues).forEach((field) => {
      setAddressValue(field, "");
    });
  }, [addressControl, resetAddressForm, setAddressValue]);

  // Step 1 submit: validate and show address section (API version)
  const handleInitialSubmit = async (data) => {
    const isValid = await triggerRegistration();
    if (isValid) {
      const registrationValues = registrationControl._formValues;
      const registerPayload = {
        licenceCode: registrationValues.registerLicenceCode || "",
        driversLicenceNumber:
          registrationValues.registerDriverLicenceNumber || "",
        Title: registrationValues.registerTitle || "",
        IDType: registrationValues.registerIDType || "",
        Password: registrationValues.registerPassword || "",
        PasswordConfirmation: registrationValues.registerRePassword || "",
        TermsConditions: termsChecked,
        FirstName: registrationValues.registerFirstname || "",
        LastName: registrationValues.registerLastname || "",
        IDPassportNumber: registrationValues.registerID || "",
        phoneNoCountry: registerDetails.phoneNoCountry || "",
        ContactNumber: phoneNo || "",
        EmailAddress: registrationValues.registerEmail || "",
        EmailAddressConfirmation:
          registrationValues.registerConfirmationEmail || "",
        userType: "Registered",
        accounts: [
          {
            accountNumber: registrationValues.account || "",
          },
        ],
      };
      try {
        setIsSubmitting(true);
        setRegisterError("");
        const response = await ApiConsumer.post(
          API_ROUTES.AUTHENTICATION.REGISTER,
          registerPayload
        );
        if (response.status === 200) {
          // Remove any address fields from registration data
          setApiToken(response.data.token || "");
          const {
            homeAddress,
            homeAddressLine1,
            homeAddressLine2,
            homeCity,
            homeProvince,
            homePostCode,
            billingAddress,
            billingAddressLine1,
            billingAddressLine2,
            billingCity,
            billingProvince,
            billingPostCode,
            ...cleanRegistrationValues
          } = registrationValues;

          setRegisterDetails(cleanRegistrationValues);

          // Clear the address form
          clearAddressForm();

          // Force remount of address form
          setFormKey((prev) => prev + 1);

          // Show address section last
          setShowAddressSection(true);
        }
      } catch (error) {
        Sentry.captureException(error);
        setRegisterError(
          error?.response?.data?.error ||
            "Registration failed. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Step 2 submit: full registration
  const onAddressSubmit = async (data) => {
    try {
      const isValid = await triggerAddress();
      if (!isValid) return;

      setIsSubmitting(true);
      setRegisterError("");

      const currentDate = new Date().toISOString().split("T")[0];

      // Extract street number and name from homeAddressLine1 if not already in data
      let homeStreetNum = data.homeStreetNumber;
      let homeStreetName = data.homeStreetName;

      if (!homeStreetNum && data.homeAddressLine1) {
        const parts = data.homeAddressLine1.trim().split(/\s+/);
        if (parts.length > 1 && !isNaN(parts[0])) {
          homeStreetNum = parts[0];
          homeStreetName = parts.slice(1).join(" ");
        }
      }

      // Same for billing address if not using same as home
      let billingStreetNum = data.billingStreetNumber;
      let billingStreetName = data.billingStreetName;

      if (!useSameAddress && !billingStreetNum && data.billingAddressLine1) {
        const parts = data.billingAddressLine1.trim().split(/\s+/);
        if (parts.length > 1 && !isNaN(parts[0])) {
          billingStreetNum = parts[0];
          billingStreetName = parts.slice(1).join(" ");
        }
      }

      const updatePayload = {
        Action: "UPDATE",
        userType: "Registered",
        errorMessage: "LOGIN SUCCESSFUL",
        openSnappdriveBookings: [],
        apiToken: null,
        refreshToken: null,
        title: registerDetails.registerTitle || "",
        firstName: registerDetails.registerFirstname || "",
        lastName: registerDetails.registerLastname || "",
        idType: registerDetails.registerIDType || "",
        idPassportNumber: registerDetails.registerID || "",
        driversLicenceNumber: registerDetails.registerDriverLicenceNumber || "",
        licenceIssueDate: currentDate,
        licenceExpiryDate: currentDate,
        licenceCode: registerDetails.registerLicenceCode || "",
        contactNumber: phoneNo || "",
        emailAddress: registerDetails.registerEmail || "",
        token: apiToken || "",
        cardToken: "",
        billingAddress1: useSameAddress
          ? data.homeAddressLine1 ||
            `${homeStreetNum || ""} ${homeStreetName || ""}`.trim()
          : data.billingAddressLine1 ||
            `${billingStreetNum || ""} ${billingStreetName || ""}`.trim(),
        billingAddress2: useSameAddress
          ? data.homeSuburb || data.homeCity || ""
          : data.billingSuburb || data.billingCity || "",
        billingAddress3: useSameAddress
          ? [data.homeCity, data.homeProvince].filter(Boolean).join(", ")
          : [data.billingCity, data.billingProvince].filter(Boolean).join(", "),
        billingPostCode: useSameAddress
          ? data.homePostCode || ""
          : data.billingPostCode || "",
        billingCountry: useSameAddress ? data.homeCountry : data.billingCountry,
        addressCountry: data.homeCountry,
        unitNumber: data.homeUnitNumber || "",
        buildingComplexName: data.homeBuildingComplex || "",
        streetNumber:
          homeStreetNum || data.homeAddressLine1?.split(" ")[0] || "",
        streetName:
          homeStreetName ||
          data.homeAddressLine1?.split(" ").slice(1).join(" ") ||
          "",
        suburb: data.homeSuburb || data.homeCity || "",
        province: data.homeProvince || "",
        city: data.homeCity || "",
        postCode: data.homePostCode || "",
      };

      const response = await ApiConsumer.put(
        API_ROUTES.AUTHENTICATION.UPDATE(registerDetails.registerEmail),
        updatePayload
      );

      if (response.status === 200) {
        // Get token from response
        const token = response.data.token;

        // Make OTP verification call
        const otpResponse = await ApiConsumer.post(
          API_ROUTES.AUTHENTICATION.VERIFY_ACCOUNT(
            registerDetails.registerEmail
          ),
          {
            contactNumber: phoneNo,
            deviceId: "",
            token: "",
            userType: "",
          }
        );

        if (
          otpResponse.data.action.includes("SENDOTP") &&
          otpResponse.status === 200
        ) {
          // Navigate to OTP page with required data
          history.push("/otp", {
            state: {
              email: registerDetails.registerEmail,
              token: token,
              payload: updatePayload,
              phoneNo: phoneNo,
            },
          });
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      setRegisterError(
        error?.response?.data?.error || "Registration failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  // Helper for opening modals
  const openPagePostModal = (slug) => {
    setVisibleModal(CONSTANTS.MODAL_TYPE.PAGEPOST);
    setPagePostModalSlug(slug);
  };

  return (
    <Fragment>
      <Modals
        visibleModal={visibleModal}
        setVisibleModal={setVisibleModal}
        loginAccountType={loginAccountType}
        setLoginAccountType={setLoginAccountType}
        pagePostModalSlug={pagePostModalSlug}
        setPagePostModalSlug={setPagePostModalSlug}
      />
      <Header />
      <Helmet>
        <title>Register | Bluu Car Rental</title>
      </Helmet>
      <div className="bg-gray-200">
        {/* Header Section */}
        <div className="w-full bg-gray-200 text-center">
          <div className="container m-auto py-6 max-w-screen-xl">
            <div className="col-span-12">
              <h1 className="text-3xl lg:text-4xl px-4 lg:p-4 text-blue-900 font-bold">
                {showAddressSection
                  ? "Complete Your Registration"
                  : "Create an online profile"}
              </h1>
              <p className="text-gray-600 mt-2">
                {showAddressSection
                  ? "Please provide your address information to complete your registration."
                  : "Fill in the form below to create a Bluu Car Rental online profile. This profile allows you to manage your quotes and reservations."}
              </p>
            </div>
          </div>
        </div>
        {/* Main Content */}

        <div className="relative bg-gray-200 pb-10 px-4">
          <div className="mx-auto max-w-2xl">
            <form
              onSubmit={
                showAddressSection
                  ? handleAddressSubmit(onAddressSubmit)
                  : handleRegistrationSubmit(handleInitialSubmit)
              }
            >
              <div className="grid grid-cols-2 gap-4 p-6 lg:p-8 rounded-lg bg-white shadow-lg">
                {!showAddressSection ? (
                  <>
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Account: <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="account"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="account"
                            name="account"
                            type="text"
                            className={`style-input ${
                              registrationErrors.account
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prevState) => ({
                                ...prevState,
                                account: e.target.value,
                              }));
                            }}
                            value={registerDetails.account}
                          />
                        )}
                      />
                      {registrationErrors.account && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.account.message}
                        </p>
                      )}
                    </div>
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        ID type: <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerIDType"
                        control={registrationControl}
                        render={({ field }) => (
                          <select
                            {...field}
                            id="registerIDType"
                            autoComplete="registerIDType"
                            className={`style-select ${
                              registrationErrors.registerIDType
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prevState) => ({
                                ...prevState,
                                registerIDType: e.target.value,
                              }));
                            }}
                            value={registerDetails.registerIDType}
                          >
                            <option value="selected" selected disabled hidden>
                              Select option
                            </option>
                            <option value="SAID">SA ID</option>
                            <option value="PASS">Passport</option>
                            <option value="NAID">Namibian ID</option>
                            <option value="BWID">Botswana ID</option>
                          </select>
                        )}
                      />
                      {registrationErrors.registerIDType && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerIDType.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        ID/Passport number:{" "}
                        <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerID"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`style-input w-full mt-1 ${
                              registrationErrors.registerID
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prev) => ({
                                ...prev,
                                registerID: e.target.value,
                              }));
                            }}
                          />
                        )}
                      />
                      {registrationErrors.registerID && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerID.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Title: <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerTitle"
                        control={registrationControl}
                        render={({ field }) => (
                          <select
                            {...field}
                            id="registerTitle"
                            autoComplete="registerTitle"
                            className={`style-select ${
                              registrationErrors.registerTitle
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prevState) => ({
                                ...prevState,
                                registerTitle: e.target.value,
                              }));
                            }}
                            value={registerDetails.registerTitle}
                          >
                            <option value="selected" selected disabled hidden>
                              Select option
                            </option>
                            <option value="Mr">MR</option>
                            <option value="Mrs">MRS</option>
                            <option value="Mx">MX</option>
                            <option value="Miss">MISS</option>
                            <option value="Ms">MS</option>
                            <option value="Prof">PROF</option>
                            <option value="Dr">DR</option>
                            <option value="Sir">SIR</option>
                          </select>
                        )}
                      />
                      {registrationErrors.registerTitle && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerTitle.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        First name: <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerFirstname"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="registerFirstname"
                            name="registerFirstname"
                            type="text"
                            className={`style-input ${
                              registrationErrors.registerFirstname
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prevState) => ({
                                ...prevState,
                                registerFirstname: e.target.value,
                              }));
                            }}
                            value={registerDetails.registerFirstname}
                          />
                        )}
                      />
                      {registrationErrors.registerFirstname && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerFirstname.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Last name: <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerLastname"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="registerLastname"
                            name="registerLastname"
                            type="text"
                            className={`style-input ${
                              registrationErrors.registerLastname
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prevState) => ({
                                ...prevState,
                                registerLastname: e.target.value,
                              }));
                            }}
                            value={registerDetails.registerLastname}
                          />
                        )}
                      />
                      {registrationErrors.registerLastname && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerLastname.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        CellPhone: <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerCellNumber"
                        control={registrationControl}
                        render={({ field }) => (
                          <PhoneInput
                            {...field}
                            placeholder="Enter phone number"
                            onChange={(value, country) => {
                              const phoneNumber = parsePhoneNumberFromString(
                                value,
                                country.countryCode.toUpperCase()
                              );

                              if (phoneNumber) {
                                // Get the international formatted number
                                const formattedNumber =
                                  phoneNumber.formatInternational();

                                // Extract the country code and number from the formatted number
                                const [countryCode, ...numberParts] =
                                  formattedNumber.split(" ");
                                const phoneNumberWithoutCountryCode =
                                  numberParts.join("");

                                // Combine the country code and phone number with a hyphen
                                const formattedWithHyphen = `${countryCode}-${phoneNumberWithoutCountryCode}`;

                                // Update the state
                                field.onChange(formattedWithHyphen); // Save formatted number with hyphen
                                setPhoneNo(formattedWithHyphen);

                                // Set phone number country in register payload
                                setRegisterDetails((prev) => ({
                                  ...prev,
                                  phoneNoCountry:
                                    country.countryCode.toUpperCase(),
                                }));
                              } else {
                                field.onChange(field.value); // Fallback to raw value
                              }
                            }}
                            countryCodeEditable={false}
                            country="za"
                            withCountryCallingCode
                            containerClass="w-full"
                            inputClass="mt-1 px-3 h-12 block w-full focus:outline-none rounded-full"
                            buttonClass="bg-transparent border-none text-gray-700"
                            dropdownClass="bg-white border rounded-md shadow-lg"
                            searchClass="border rounded-md p-2 text-gray-700"
                            inputProps={{
                              required: true,
                            }}
                            enableSearch={true}
                            containerStyle={{
                              maxWidth: "100%",
                              width: "400px",
                              height: "48px",
                              display: "flex",
                              alignItems: "center",
                            }}
                            inputStyle={{
                              borderRadius: "9999px",
                              borderColor: registrationErrors.registerCellNumber
                                ? "#FF0000"
                                : "#D1D5DB",
                              color: "#333333",
                              height: "100%",
                              width: "100%",
                              paddingLeft: "4rem",
                              paddingRight: "0.75rem",
                            }}
                            buttonStyle={{
                              padding: "0.5rem",
                              borderRadius: "9999px 0 0 9999px",
                              backgroundColor: "transparent",
                              color: "#333333",
                            }}
                            dropdownStyle={{
                              borderRadius: "8px",
                              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                            searchStyle={{
                              padding: "0.5rem",
                              backgroundColor: "#f3f4f6",
                              borderRadius: "8px",
                            }}
                            value={registerDetails.registerCellNumber} // Syncing the value from local state
                          />
                        )}
                      />
                      {registrationErrors.registerCellNumber && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerCellNumber.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Driving Licence Code:{" "}
                        <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerLicenceCode"
                        control={registrationControl}
                        render={({ field }) => (
                          <select
                            {...field}
                            id="registerLicenceCode"
                            autoComplete="registerLicenceCode"
                            className={`style-select ${
                              registrationErrors.registerLicenceCode
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prevState) => ({
                                ...prevState,
                                registerLicenceCode: e.target.value,
                              }));
                            }}
                            value={registerDetails.registerLicenceCode}
                          >
                            <option value="selected" selected disabled hidden>
                              Select option
                            </option>
                            <option value="B">B</option>
                            <option value="C1">C1</option>
                            <option value="C">C</option>
                            <option value="EB">EB</option>
                            <option value="EC1">EC1</option>
                            <option value="EC">EC</option>
                            <option value="INT">INT (International)</option>
                          </select>
                        )}
                      />
                      {registrationErrors.registerLicenceCode && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerLicenceCode.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Driver Licence Number:{" "}
                        <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerDriverLicenceNumber"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="registerDriverLicenceNumber"
                            name="registerDriverLicenceNumber"
                            type="tel"
                            className={`style-input w-full mt-1 ${
                              registrationErrors.registerDriverLicenceNumber
                                ? "border-red-700"
                                : "border-gray-300"
                            }`}
                            onChange={(e) => {
                              field.onChange(e); // Update the field value in react-hook-form
                              setRegisterDetails((prevState) => ({
                                ...prevState,
                                registerDriverLicenceNumber: e.target.value,
                              }));
                            }}
                            value={registerDetails.registerDriverLicenceNumber}
                          />
                        )}
                      />
                      {registrationErrors.registerDriverLicenceNumber && (
                        <p className="text-red-500 text-sm mt-1">
                          {
                            registrationErrors.registerDriverLicenceNumber
                              .message
                          }
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Email Address <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerEmail"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            type="email"
                            className={`style-input w-full mt-1 ${
                              registrationErrors.registerEmail
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prev) => ({
                                ...prev,
                                registerEmail: e.target.value,
                              }));
                            }}
                          />
                        )}
                      />
                      {registrationErrors.registerEmail && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerEmail.message}
                        </p>
                      )}
                    </div>
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Confirm Email address{" "}
                        <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerConfirmationEmail"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            type="email"
                            className={`style-input w-full mt-1 ${
                              registrationErrors.registerConfirmationEmail
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setRegisterDetails((prev) => ({
                                ...prev,
                                registerConfirmationEmail: e.target.value,
                              }));
                            }}
                          />
                        )}
                      />
                      {registrationErrors.registerConfirmationEmail && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerConfirmationEmail.message}
                        </p>
                      )}
                    </div>
                    <div className="mt-2 relative">
                      <label className="block text-sm font-medium text-gray-700">
                        Password <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerPassword"
                        control={registrationControl}
                        render={({ field }) => (
                          <div className="relative w-full">
                            <input
                              type={showConfirmPassword ? "text" : "password"} // Toggle password visibility
                              className={`style-input w-full mt-1 pr-10 ${
                                registrationErrors.registerPassword
                                  ? "border-red-700"
                                  : "border-gray-300"
                              }`} // Add padding-right to make space for the icon
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                setRegisterDetails((prev) => ({
                                  ...prev,
                                  registerPassword: e.target.value,
                                }));
                              }}
                            />
                            {/* Password visibility toggle icon
												<span
													onClick={
														togglePasswordVisibility
													}
													className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
												>
													<FontAwesomeIcon
														icon={
															showPassword
																? faEyeSlash
																: faEye
														}
														size="lg"
														className="text-gray-500"
													/>
												</span> */}
                          </div>
                        )}
                      />
                      {registrationErrors.registerPassword && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerPassword.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2 relative">
                      {/* Add relative positioning to this div */}
                      <label
                        htmlFor="registerRePassword"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Re-enter password:{" "}
                        <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="registerRePassword"
                        control={registrationControl}
                        render={({ field }) => (
                          <div className="relative w-full">
                            <input
                              {...field}
                              id="registerRePassword"
                              type={showConfirmPassword ? "text" : "password"} // Toggle password visibility
                              className={`style-input w-full mt-1 pr-10 ${
                                registrationErrors.registerRePassword
                                  ? "border-red-700"
                                  : "border-gray-300"
                              }`} // Add padding-right to make space for the icon
                            />
                            {/* Password visibility toggle icon */}
                            <span
                              onClick={toggleConfirmPasswordVisibility}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                            >
                              <FontAwesomeIcon
                                icon={showConfirmPassword ? faEyeSlash : faEye}
                                size="lg"
                                className="text-gray-500"
                              />
                            </span>
                          </div>
                        )}
                      />
                      {registrationErrors.registerRePassword && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.registerRePassword.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-2">
                      <label
                        htmlFor="consent"
                        className="block text-sm font-medium text-gray-700"
                      >
                        I have read and accept the{" "}
                        <span
                          onClick={() =>
                            openPagePostModal("terms-and-conditions")
                          }
                          className="text-blue-400 hover:underline cursor-pointer"
                        >
                          Website Terms and Conditions
                        </span>
                        <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="consent"
                        control={registrationControl}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="consent"
                            type="checkbox"
                            className="h-4 w-4 mt-0.5 cursor-pointer"
                            checked={field.value}
                            onChange={(event) => {
                              const isChecked = event.target.checked;
                              field.onChange(isChecked);
                              setTermsChecked(isChecked);
                            }}
                          />
                        )}
                      />
                      {registrationErrors.consent && (
                        <p className="text-red-500 text-sm mt-1">
                          {registrationErrors.consent.message}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div key={formKey} className="col-span-2">
                    <div className="col-span-2 mt-8">
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">
                        Home Address
                      </h2>
                    </div>
                    <div className="col-span-2">
                      <Controller
                        name="homeAddress"
                        control={addressControl}
                        render={({ field }) => (
                          <div className="relative">
                            <GooglePlacesAutocomplete
                              section={"home"}
                              field={"homeAddress"}
                              isMandatory={true}
                              setPlaceId={(placeId) => {
                                field.onChange(placeId);
                              }}
                              onAddressSelect={(addressComponents) => {
                                // For home address
                                const homeUpdates = {
                                  homeAddressLine1:
                                    addressComponents.streetNumber &&
                                    addressComponents.streetName
                                      ? `${addressComponents.streetNumber} ${addressComponents.streetName}`.trim()
                                      : "",
                                  homeStreetNumber:
                                    addressComponents.streetNumber || "",
                                  homeStreetName:
                                    addressComponents.streetName || "",
                                  homeSuburb: addressComponents.suburb || "",
                                  homeCity: addressComponents.city || "",
                                  homeProvince:
                                    addressComponents.province || "",
                                  homePostCode:
                                    addressComponents.postalCode || "",
                                  homeCountry: addressComponents.country,
                                };
                                console.log(
                                  "Home address country:",
                                  addressComponents.country
                                ); // Debug log
                                Object.entries(homeUpdates).forEach(
                                  ([fieldName, value]) => {
                                    setAddressValue(fieldName, value, {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    });
                                  }
                                );

                                // Update the form state with the complete address string
                                setAddressValue(
                                  "homeAddress",
                                  addressComponents.streetNumber &&
                                    addressComponents.streetName
                                    ? `${addressComponents.streetNumber} ${addressComponents.streetName}`.trim()
                                    : ""
                                );
                              }}
                            />
                            {addressErrors.homeAddress && (
                              <p className="text-red-500 text-sm mt-1">
                                {addressErrors.homeAddress.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Address Line 1 <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="homeAddressLine1"
                        control={addressControl}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`style-input ${
                              addressErrors.homeAddressLine1
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                          />
                        )}
                      />
                      {addressErrors.homeAddressLine1 && (
                        <p className="text-red-500 text-sm mt-1">
                          {addressErrors.homeAddressLine1.message}
                        </p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Address Line 2
                      </label>
                      <Controller
                        name="homeAddressLine2"
                        control={addressControl}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`style-input ${
                              addressErrors.homeAddressLine2
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                          />
                        )}
                      />
                      {addressErrors.homeAddressLine2 && (
                        <p className="text-red-500 text-sm mt-1">
                          {addressErrors.homeAddressLine2.message}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700">
                        City <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="homeCity"
                        control={addressControl}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`style-input ${
                              addressErrors.homeCity
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                          />
                        )}
                      />
                      {addressErrors.homeCity && (
                        <p className="text-red-500 text-sm mt-1">
                          {addressErrors.homeCity.message}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Province <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="homeProvince"
                        control={addressControl}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`style-input ${
                              addressErrors.homeProvince
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                          />
                        )}
                      />
                      {addressErrors.homeProvince && (
                        <p className="text-red-500 text-sm mt-1">
                          {addressErrors.homeProvince.message}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Postal Code <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="homePostCode"
                        control={addressControl}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`style-input ${
                              addressErrors.homePostCode
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                          />
                        )}
                      />
                      {addressErrors.homePostCode && (
                        <p className="text-red-500 text-sm mt-1">
                          {addressErrors.homePostCode.message}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Country <span className="text-red-600">*</span>
                      </label>
                      <Controller
                        name="homeCountry"
                        control={addressControl}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`style-input ${
                              addressErrors.homeCountry
                                ? `border-red-700`
                                : `border-gray-300`
                            }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                          />
                        )}
                      />
                      {addressErrors.homeCountry && (
                        <p className="text-red-500 text-sm mt-1">
                          {addressErrors.homeCountry.message}
                        </p>
                      )}
                    </div>
                    <div className="col-span-2 mt-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={useSameAddress}
                          onChange={(e) => setUseSameAddress(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          Use the same address for billing
                        </span>
                      </label>
                    </div>
                    {/* Billing Address Step */}
                    {!useSameAddress && (
                      <>
                        <div className="col-span-2 mt-8">
                          <h2 className="text-xl font-semibold text-gray-900 mb-4">
                            Billing Address
                          </h2>
                        </div>
                        <div className="col-span-2">
                          <Controller
                            name="billingAddress"
                            control={addressControl}
                            render={({ field }) => (
                              <div className="relative">
                                <GooglePlacesAutocomplete
                                  section={"billing"}
                                  field={"billingAddress"}
                                  isMandatory={true}
                                  setPlaceId={(placeId) => {
                                    field.onChange(placeId);
                                  }}
                                  onAddressSelect={(addressComponents) => {
                                    console.log(
                                      "Billing address country:",
                                      addressComponents.country
                                    ); // Debug log
                                    const billingUpdates = {
                                      billingAddressLine1:
                                        `${addressComponents.streetNumber} ${addressComponents.streetName}`.trim(),
                                      billingCity: addressComponents.city || "",
                                      billingProvince:
                                        addressComponents.province || "",
                                      billingPostCode:
                                        addressComponents.postalCode || "",
                                      billingCountry: addressComponents.country,
                                    };
                                    Object.entries(billingUpdates).forEach(
                                      ([fieldName, value]) => {
                                        setAddressValue(fieldName, value, {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                          shouldTouch: true,
                                        });
                                      }
                                    );
                                  }}
                                />
                                {addressErrors.billingAddress && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {addressErrors.billingAddress.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Address Line 1{" "}
                            <span className="text-red-600">*</span>
                          </label>
                          <Controller
                            name="billingAddressLine1"
                            control={addressControl}
                            render={({ field }) => (
                              <input
                                type="text"
                                className={`style-input ${
                                  addressErrors.billingAddressLine1
                                    ? `border-red-700`
                                    : `border-gray-300`
                                }`}
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                              />
                            )}
                          />
                          {addressErrors.billingAddressLine1 && (
                            <p className="text-red-500 text-sm mt-1">
                              {addressErrors.billingAddressLine1.message}
                            </p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Address Line 2
                          </label>
                          <Controller
                            name="billingAddressLine2"
                            control={addressControl}
                            render={({ field }) => (
                              <input
                                type="text"
                                className={`style-input ${
                                  addressErrors.billingAddressLine2
                                    ? `border-red-700`
                                    : `border-gray-300`
                                }`}
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                              />
                            )}
                          />
                          {addressErrors.billingAddressLine2 && (
                            <p className="text-red-500 text-sm mt-1">
                              {addressErrors.billingAddressLine2.message}
                            </p>
                          )}
                        </div>
                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700">
                            City <span className="text-red-600">*</span>
                          </label>
                          <Controller
                            name="billingCity"
                            control={addressControl}
                            render={({ field }) => (
                              <input
                                type="text"
                                className={`style-input ${
                                  addressErrors.billingCity
                                    ? `border-red-700`
                                    : `border-gray-300`
                                }`}
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                              />
                            )}
                          />
                          {addressErrors.billingCity && (
                            <p className="text-red-500 text-sm mt-1">
                              {addressErrors.billingCity.message}
                            </p>
                          )}
                        </div>
                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Province <span className="text-red-600">*</span>
                          </label>
                          <Controller
                            name="billingProvince"
                            control={addressControl}
                            render={({ field }) => (
                              <input
                                type="text"
                                className={`style-input ${
                                  addressErrors.billingProvince
                                    ? `border-red-700`
                                    : `border-gray-300`
                                }`}
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                              />
                            )}
                          />
                          {addressErrors.billingProvince && (
                            <p className="text-red-500 text-sm mt-1">
                              {addressErrors.billingProvince.message}
                            </p>
                          )}
                        </div>
                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Postal Code <span className="text-red-600">*</span>
                          </label>
                          <Controller
                            name="billingPostCode"
                            control={addressControl}
                            render={({ field }) => (
                              <input
                                type="text"
                                className={`style-input ${
                                  addressErrors.billingPostCode
                                    ? `border-red-700`
                                    : `border-gray-300`
                                }`}
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                              />
                            )}
                          />
                          {addressErrors.billingPostCode && (
                            <p className="text-red-500 text-sm mt-1">
                              {addressErrors.billingPostCode.message}
                            </p>
                          )}
                        </div>
                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Country <span className="text-red-600">*</span>
                          </label>
                          <Controller
                            name="billingCountry"
                            control={addressControl}
                            render={({ field }) => (
                              <input
                                type="text"
                                className={`style-input ${
                                  addressErrors.billingCountry
                                    ? `border-red-700`
                                    : `border-gray-300`
                                }`}
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                              />
                            )}
                          />
                          {addressErrors.billingCountry && (
                            <p className="text-red-500 text-sm mt-1">
                              {addressErrors.billingCountry.message}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="flex justify-center items-center col-span-2 text-red-500 text-sm mt-1">
                  {registerError}
                </div>

                <div className="flex justify-center items-center col-span-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-1/2 px-4 py-2.5 border border-transparent rounded-md shadow-sm text-base font-medium text-white transition-colors 
      ${isSubmitting ? "bg-blue-700" : "bg-blue-900 hover:bg-blue-800"}
      flex items-center justify-center space-x-2`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>
                          {showAddressSection
                            ? "Completing Registration..."
                            : "Registering..."}
                        </span>
                      </>
                    ) : (
                      <span>
                        {showAddressSection
                          ? "Complete Registration"
                          : "Register Account"}
                      </span>
                    )}
                  </button>
                </div>

                <div className="flex justify-center items-center col-span-2">
                  <hr className="border-b border-gray-300 mt-2 w-full" />
                </div>

                <div className="my-0 text-gray-500 text-sm flex justify-center items-center col-span-2">
                  Your email address is 100% safe with us.{" "}
                  <span
                    onClick={() => openPagePostModal("privacy-policy")}
                    className="text-blue-400 hover:underline cursor-pointer"
                  >
                    Read Privacy Policy.
                  </span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </Fragment>
  );
};

export default RegisterPage;
